import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { sales as frSales } from '../../../../../locales/fr/sales';
import { sales as esSales } from '../../../../../locales/es/sales';
import { sales as arSales } from '../../../../../locales/ar/sales';
import { SaleService } from '../../../../../services/AppDataServices/services/sale.service';
import DataTable from '../../../components/DataTable';
import FilterSection from '../../../components/DataTable/FilterSection';
import useSearch from '../../../hooks/useSearch';
import PrintableView from '../../../components/DataTable/PrintableView';
import useMyAppStore from '../../../../../stores/myAppStore';
import { formatDate, formatCurrency } from '../../../../../utils';

const translations = {
  fr: frSales,
  es: esSales,
  ar: arSales
};

const Sales = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang];
  const { setPageTitle } = useMyAppStore();
  const saleService = new SaleService();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // État pour la pagination et les filtres
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    paymentMethod: 'all',
    paymentStatus: 'all'
  });
  const [showSecondary, setShowSecondary] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'desc'
  });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  // Configuration de la recherche
  const searchConfig = useMemo(() => ({
    minMatchLength: 2,
    fields: [
      { key: 'num', type: 'text' },
      { 
        key: 'customer', 
        type: 'object', 
        searchFields: ['name', 'phone', 'address']
      }
    ]
  }), []);

  const { searchItems } = useSearch(searchConfig);

  // Handlers pour les filtres
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setCurrentPage(1);
  }, []);

  const handlePaymentMethodChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, paymentMethod: value }));
    setCurrentPage(1);
  }, []);

  const handlePaymentStatusChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, paymentStatus: value }));
    setCurrentPage(1);
  }, []);

  const handleDateRangeChange = useCallback(({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
    setCurrentPage(1);
  }, []);

  const handleStartDateChange = useCallback((value) => {
    setDateRange(prev => ({ ...prev, startDate: value }));
    setCurrentPage(1);
  }, []);

  const handleEndDateChange = useCallback((value) => {
    setDateRange(prev => ({ ...prev, endDate: value }));
    setCurrentPage(1);
  }, []);

  const toggleSecondary = useCallback(() => {
    setShowSecondary(prev => !prev);
  }, []);

  // Configuration des filtres primaires
  const primaryFilters = useMemo(() => [
    {
      type: 'dateRange',
      key: 'dateRange',
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      onStartDateChange: handleStartDateChange,
      onEndDateChange: handleEndDateChange,
      onChange: handleDateRangeChange
    },
    {
      type: 'search',
      key: 'search',
      label: t.filters.search.label,
      placeholder: t.filters.search.placeholder,
      value: search,
      onChange: handleSearchChange,
      useAzerty: true
    },
    {
      type: 'sort',
      key: 'sort',
      label: t.filters.sort?.label || 'Trier par',
      value: sortConfig.key,
      direction: sortConfig.direction,
      onChange: (value) => {
        setSortConfig(prev => ({
          key: value,
          direction: prev.key === value && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
      },
      options: [
        { value: 'num', label: t.columns.num || 'N° Vente' },
        { value: 'date', label: t.columns.date || 'Date' },
        { value: 'customer.name', label: t.columns.customer || 'Client' },
        { value: 'montant.total', label: t.columns.total || 'Total' },
        { value: 'status', label: t.columns.status || 'Statut' }
      ]
    }
  ], [t, search, handleSearchChange, sortConfig, dateRange, handleStartDateChange, handleEndDateChange, handleDateRangeChange]);

  // Configuration des filtres secondaires
  const secondaryFilters = useMemo(() => [
    {
      type: 'select',
      key: 'status',
      label: t.filters.status.label,
      value: filters.status,
      onChange: handleStatusChange,
      options: [
        { value: 'all', label: t.filters.status.options.all || 'Tous' },
        { value: 'draft', label: t.filters.status.options.draft || 'Brouillon' },
        { value: 'pending', label: t.filters.status.options.pending || 'En attente' },
        { value: 'completed', label: t.filters.status.options.completed || 'Terminée' },
        { value: 'cancelled', label: t.filters.status.options.cancelled || 'Annulée' }
      ]
    },
    {
      type: 'select',
      key: 'paymentMethod',
      label: t.filters.paymentMethod.label,
      value: filters.paymentMethod,
      onChange: handlePaymentMethodChange,
      options: [
        { value: 'all', label: t.filters.paymentMethod.options.all || 'Tous' },
        { value: 'cash', label: t.filters.paymentMethod.options.cash || 'Espèces' },
        { value: 'card', label: t.filters.paymentMethod.options.card || 'Carte' },
        { value: 'transfer', label: t.filters.paymentMethod.options.transfer || 'Virement' },
        { value: 'check', label: t.filters.paymentMethod.options.check || 'Chèque' },
        { value: 'mobile', label: t.filters.paymentMethod.options.mobile || 'Mobile' }
      ]
    },
    {
      type: 'select',
      key: 'paymentStatus',
      label: t.filters.paymentStatus.label,
      value: filters.paymentStatus,
      onChange: handlePaymentStatusChange,
      options: [
        { value: 'all', label: t.filters.paymentStatus.options.all || 'Tous' },
        { value: 'pending', label: t.filters.paymentStatus.options.pending || 'En attente' },
        { value: 'partial', label: t.filters.paymentStatus.options.partial || 'Partiel' },
        { value: 'paid', label: t.filters.paymentStatus.options.paid || 'Payé' }
      ]
    }
  ], [t, filters, handleStatusChange, handlePaymentMethodChange, handlePaymentStatusChange]);

  // Configuration des colonnes
  const columns = useMemo(() => [
    {
      key: 'num',
      label: t.columns.num || 'N° Vente',
      sortable: true,
      visible: true
    },
    {
      key: 'date',
      label: t.columns.date || 'Date',
      sortable: true,
      visible: true,
      render: (item) => formatDate(item.date, { includeTime: false })
    },
    {
      key: 'customer',
      label: t.columns.customer || 'Client',
      sortable: true,
      visible: true,
      render: (item) => item.customer?.name || '-'
    },
    {
      key: 'montant',
      label: t.columns.total || 'Total',
      sortable: true,
      visible: true,
      render: (item) => formatCurrency(item.montant.total)
    },
    {
      key: 'status',
      label: t.columns.status || 'Statut',
      sortable: true,
      visible: true,
      render: (item) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          item.status === 'completed' ? 'bg-green-100 text-green-800' :
          item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          item.status === 'draft' ? 'bg-gray-100 text-gray-800' :
          item.status === 'cancelled' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {t.filters.status.options[item.status] || item.status}
        </span>
      )
    },
    {
      key: 'paiement',
      label: t.columns.paymentMethod || 'Méthode de paiement',
      sortable: true,
      visible: true,
      render: (item) => (
        <div className="space-y-1">
          <div>{t.filters.paymentMethod.options[item.paiement.method] || item.paiement.method}</div>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            item.paiement.status === 'paid' ? 'bg-green-100 text-green-800' :
            item.paiement.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {t.filters.paymentStatus.options[item.paiement.status] || item.paiement.status}
          </span>
        </div>
      )
    },
    {
      key: 'items',
      label: t.columns.items || 'Articles',
      sortable: false,
      visible: true,
      render: (item) => `${item.items.length} article(s)`
    }
  ], [t]);

  // Configuration des colonnes collapsibles
  const collapsibleColumns = useMemo(() => [
    {
      key: 'basicInfo',
      label: t.columns.basicInfo || 'Informations de base',
      render: (item) => (
        <dl className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.num || 'N° Vente'}</dt>
            <dd className="flex-1">{item.num || 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.date || 'Date'}</dt>
            <dd className="flex-1">{formatDate(item.date)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.customer || 'Client'}</dt>
            <dd className="flex-1">{item.customer?.name || 'Non disponible'}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.status || 'Statut'}</dt>
            <dd className="flex-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                item.status === 'completed' ? 'bg-green-100 text-green-800' :
                item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                item.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {t.filters.status.options[item.status] || item.status}
              </span>
            </dd>
          </div>
        </dl>
      )
    },
    {
      key: 'financialInfo',
      label: t.columns.financialInfo || 'Détails financiers',
      render: (item) => (
        <dl className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.total || 'Total'}</dt>
            <dd className="flex-1">{formatCurrency(item.montant.total)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.paymentMethod || 'Méthode de paiement'}</dt>
            <dd className="flex-1">{t.filters.paymentMethod.options[item.paiement.method] || item.paiement.method}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.paymentStatus || 'État du paiement'}</dt>
            <dd className="flex-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                item.paiement.status === 'paid' ? 'bg-green-100 text-green-800' :
                item.paiement.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {t.filters.paymentStatus.options[item.paiement.status] || item.paiement.status}
              </span>
            </dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.items || 'Articles'}</dt>
            <dd className="flex-1">{item.items.length} article(s)</dd>
          </div>
        </dl>
      )
    },
    {
      key: 'systemInfo',
      label: t.columns.systemInfo || 'Informations système',
      render: (item) => (
        <dl className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.createdAt || 'Date de création'}</dt>
            <dd className="flex-1">{formatDate(item.createdAt)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="text-gray-500 min-w-[120px]">{t.columns.updatedAt || 'Dernière modification'}</dt>
            <dd className="flex-1">{formatDate(item.updatedAt)}</dd>
          </div>
        </dl>
      )
    }
  ], [t, formatCurrency]);

  // Fonction de récupération des données
  const fetchData = useCallback(async ({ page, limit, search, filters, sortConfig }) => {
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer les ventes avec la période
      let sales = await saleService.getSales(
        new Date(dateRange.startDate),
        new Date(dateRange.endDate)
      );
      let total = sales.length;

      // Application de la recherche
      if (search) {
        sales = searchItems(search, sales);
      }

      // Application des filtres
      if (filters.status && filters.status !== 'all') {
        sales = sales.filter(sale => sale.status === filters.status);
      }

      if (filters.paymentMethod && filters.paymentMethod !== 'all') {
        sales = sales.filter(sale => sale.paiement.method === filters.paymentMethod);
      }

      if (filters.paymentStatus && filters.paymentStatus !== 'all') {
        sales = sales.filter(sale => sale.paiement.status === filters.paymentStatus);
      }

      // Application du tri
      if (sortConfig.key) {
        sales.sort((a, b) => {
          let aValue, bValue;
          
          // Gestion des chemins imbriqués
          if (sortConfig.key.includes('.')) {
            const [parent, child] = sortConfig.key.split('.');
            aValue = a[parent]?.[child];
            bValue = b[parent]?.[child];
          } else {
            aValue = a[sortConfig.key];
            bValue = b[sortConfig.key];
          }

          const direction = sortConfig.direction === 'asc' ? 1 : -1;
          
          if (typeof aValue === 'string') {
            return direction * aValue.localeCompare(bValue);
          }
          return direction * (aValue - bValue);
        });
      }

      // Mise à jour du total
      total = sales.length;

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      sales = sales.slice(start, end);

      setData({ items: sales, total });
    } catch (error) {
      console.error('Error fetching sales:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [searchItems, dateRange]);

  // Effet pour charger les données
  useEffect(() => {
    fetchData({
      page: currentPage,
      limit: itemsPerPage,
      search,
      filters,
      sortConfig
    });
  }, [fetchData, currentPage, itemsPerPage, search, filters, sortConfig, refreshTrigger]);

  // Handlers pour les changements d'état
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  }, []); 

  const handleSortChange = useCallback((newSortConfig) => {
    setSortConfig(newSortConfig);
  }, []);

  const handleAddNew = useCallback(() => {
    navigate('new');
  }, [navigate]);

  // Configuration des actions
  const actionConfig = useMemo(() => ({
    primary: [
      {
        label: t.actions.newSale,
        onClick: handleAddNew,
        primary: true
      }
    ],
    row: [
      {
        icon: 'edit',
        onClick: (item) => navigate(`${item.id}/edit`),
        title: t.actions.edit,
        className: 'text-blue-500 hover:text-blue-700'
      },
      {
        icon: 'delete',
        onClick: (item) => {
          if (window.confirm(t.confirmations.delete)) {
            handleDeleteSale(item);
          }
        },
        title: t.actions.delete,
        className: 'text-red-500 hover:text-red-700'
      }
    ]
  }), [t, navigate, handleAddNew]);

  // Configuration complète pour le DataTable
  const dataTableConfig = useMemo(() => ({
    tableConfig: {
      pagination: {
        pageSize: itemsPerPage,
        pageSizeOptions: [10, 25, 50, 100]
      },
      sorting: {
        defaultSort: { key: 'createdAt', direction: 'desc' }
      }
    },
    columns,
    filters: {
      primary: primaryFilters,
      secondary: secondaryFilters
    },
    actions: actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    totalItems: data.total,
    onPageChange: handlePageChange,
    onItemsPerPageChange: handleItemsPerPageChange,
    onSearch: handleSearchChange,
    onSortChange: handleSortChange,
    onFilterChange: setFilters,
    refreshData: () => setRefreshTrigger(prev => prev + 1),
    collapsibleColumns
  }), [
    columns,
    primaryFilters,
    secondaryFilters,
    actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    handleSearchChange,
    handleSortChange,
    setFilters,
    collapsibleColumns
  ]);

  // Handlers pour les opérations CRUD
  const handleDeleteSale = async (sale) => {
    try {
      await saleService.deleteSale(sale.id);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      setError(error);
    }
  };

  useEffect(() => {
    setPageTitle(t.title);
  }, [lang, t.title, setPageTitle]);

  return (
    <>
      <div className="space-y-4 print-hide">
        <FilterSection
          primaryFilters={primaryFilters}
          secondaryFilters={secondaryFilters}
          onSearch={handleSearchChange}
          onFilterChange={setFilters}
          onSortChange={handleSortChange}
          onAdd={handleAddNew}
        />
        <DataTable {...dataTableConfig} />
      </div>
      <PrintableView
        title={t.title}
        columns={columns}
        data={data.items}
        translations={t}
      />
    </>
  );
};

export default Sales; 